// RoleSwitcher — sélecteur de rôle actif pour les comptes multi-rôles WÔMA.
// Visible uniquement quand user.roles contient ≥ 2 rôles. Appelle /api/users/switch-role
// puis met à jour le localStorage et redirige vers le dashboard du nouveau rôle.

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Check, UserCircle2, Truck, Store, Palette, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ROLE_META = {
  client: { label: "Client", icon: UserCircle2, dashboard: "/home" },
  courier: { label: "Livreur / Chauffeur", icon: Truck, dashboard: "/courier" },
  partner: { label: "Partenaire", icon: Store, dashboard: "/partner" },
  artisan: { label: "Artisan", icon: Palette, dashboard: "/artisans/dashboard" },
};

// Bascule de rôle réutilisable (sélecteur + menu compte mobile)
export const useSwitchRole = (user, onUserUpdate) => {
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const switchRole = async (newRole) => {
    if (switching || !user?.id) return false;
    setSwitching(true);
    try {
      const res = await axios.post(`${API}/users/switch-role`, { user_id: user.id, role: newRole });
      const updated = res.data?.user;
      if (!updated) throw new Error("Réponse invalide");
      const merged = { ...user, ...updated, role: updated.active_role || newRole };
      if (onUserUpdate) onUserUpdate(merged);
      try { localStorage.setItem("user", JSON.stringify(merged)); } catch (e) { /* navigation privée */ }
      toast.success(`Basculé vers : ${ROLE_META[newRole]?.label || newRole}`);
      navigate(ROLE_META[newRole]?.dashboard || "/home");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de changer de rôle");
      return false;
    } finally {
      setSwitching(false);
    }
  };
  return { switchRole, switching };
};

export default function RoleSwitcher({ user, onUserUpdate, className = "" }) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const roles = Array.isArray(user?.roles) && user.roles.length > 0
    ? user.roles
    : (user?.role ? [user.role] : []);
  const activeRole = user?.active_role || user?.role || roles[0];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Si l'utilisateur n'a qu'un rôle, on cache le composant (pas de bascule possible)
  if (roles.length < 2 || !user?.id) return null;

  const handleSwitch = async (newRole) => {
    if (newRole === activeRole || switching) return;
    setSwitching(true);
    try {
      const res = await axios.post(`${API}/users/switch-role`, {
        user_id: user.id,
        role: newRole,
      });
      const updated = res.data?.user;
      if (!updated) throw new Error("Réponse invalide");
      // Important : on force le `role` legacy sur active_role pour que les gardes
      // de route (user.role === 'client') continuent à fonctionner.
      const merged = { ...user, ...updated, role: updated.active_role || newRole };
      if (onUserUpdate) onUserUpdate(merged);
      try {
        localStorage.setItem("user", JSON.stringify(merged));
      } catch (e) {
        // noop — localStorage peut être désactivé en navigation privée
      }
      toast.success(`Basculé vers : ${ROLE_META[newRole]?.label || newRole}`);
      setOpen(false);
      navigate(ROLE_META[newRole]?.dashboard || "/home");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de changer de rôle");
    } finally {
      setSwitching(false);
    }
  };

  const ActiveIcon = ROLE_META[activeRole]?.icon || UserCircle2;

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="role-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-white transition-colors disabled:opacity-60"
        data-testid="role-switcher-trigger"
      >
        {switching ? <Loader2 size={13} className="animate-spin" /> : <ActiveIcon size={13} />}
        {ROLE_META[activeRole]?.label || activeRole}
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-60 bg-[#0F172A] border border-white/15 rounded-2xl shadow-2xl shadow-black/60 z-50 overflow-hidden"
          data-testid="role-switcher-menu"
        >
          <p className="px-4 py-2 text-[9px] font-black uppercase tracking-[0.3em] text-slate-500 border-b border-white/5">
            Mes rôles
          </p>
          {roles.map((r) => {
            const Icon = ROLE_META[r]?.icon || UserCircle2;
            const active = r === activeRole;
            return (
              <button
                key={r}
                type="button"
                onClick={() => handleSwitch(r)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-bold transition-colors ${
                  active
                    ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "text-white hover:bg-white/5"
                }`}
                data-testid={`role-switcher-option-${r}`}
              >
                <Icon size={16} />
                <span className="flex-1">{ROLE_META[r]?.label || r}</span>
                {active && <Check size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
